'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { ClientOnly } from '@/components/ClientOnly';

type ToastTone = 'success' | 'error' | 'info';

type ToastItem = {
  id: number;
  message: string;
  tone: ToastTone;
};

type ToastContextValue = {
  toast: (message: string, tone?: ToastTone) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let toastId = 0;

function ToastStack({ items, dismiss }: { items: ToastItem[]; dismiss: (id: number) => void }) {
  const reduce = useReducedMotion();
  return createPortal(
    <div className="ue-toast-stack" aria-live="polite" aria-relevant="additions">
      <AnimatePresence>
        {items.map((item) => (
          <motion.div
            key={item.id}
            role={item.tone === 'error' ? 'alert' : 'status'}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className={`ue-toast ue-toast-${item.tone}`}
          >
            {item.tone === 'success' && <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
            {item.tone === 'error' && <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            {item.tone === 'info' && <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            <span className="flex-1 text-sm font-medium">{item.message}</span>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="ue-toast-dismiss"
              aria-label="Fechar notificação"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, tone: ToastTone = 'success') => {
    const id = ++toastId;
    setItems((prev) => [...prev, { id, message, tone }]);
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 3200);
  }, []);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ClientOnly>
        <ToastStack items={items} dismiss={dismiss} />
      </ClientOnly>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
